import DateUtils from 'src/app/utils/date';
import { map, mergeMap } from 'rxjs/operators';
import { Injectable } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { Endpoints } from "src/app/shared/config/endpoints";
import { GeneralService } from "../../general.service";
import { BehaviorSubject, forkJoin, Observable, of } from "rxjs";

import { PathfixConfig } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.state';

@Injectable({
  providedIn: "root",
})
export class EmailService {
  org_id;
  user_id;

  private dateUtil = new DateUtils()

  private server_uri = `${this.endpoints.communicationsEndpoint}`;
  private files_uri = `${this.endpoints.fileServiceUrl}`;
  private deals_uri = this.endpoints.salesUrl;
  private mailingUri = this.endpoints.mailServiceUrl;
  private imap_server_uri = `${this.mailingUri}${this.endpoints.mail.imapGeneric}`;

  /** BEHAVIOURALSUBJECT */
  public selectedEmail = new BehaviorSubject<any>({});
  public emailToActOn = new BehaviorSubject<any>({});
  public email_is_modal = new BehaviorSubject<any>(false);
  public user_data = new BehaviorSubject<any>({});
  public sales_item = new BehaviorSubject<any>({});

  // email_context specifying the part of the page where email feeds are needed
  public email_context = new BehaviorSubject<any>({});
  public new_email = new BehaviorSubject<any>({});
  public context_primary_email_address = new BehaviorSubject("");

  constructor(
    private http: HttpClient,
    private endpoints: Endpoints, 
    private generalService: GeneralService,
    private store: Store<AppState>,
  ) {
    //
    this.store.select('userInfo').subscribe(info => {
      if (Object.keys(info).length) {
        this.org_id = info.organization.id
        this.user_id = info.user.id;
      }
    })

    localStorage.setItem('pathfix_key', PathfixConfig.public_key);
  }

  getUserData() {
    return this.user_data.value;
  }

  setSelectedEmail(email) {
    this.selectedEmail.next(email);
  }

  /** HTTP */
  fetchAccessTokenDataFromServer() {
    const query_string = `user_id=${this.user_id}&org_id=${this.org_id}&service=gmail,office,imap`;
    return this.http.get(`${this.mailingUri}/users?${query_string}`);
  }

  saveAccessTokenOnServer(data: any) {
    const { org_id, user_id } = this;
    return this.http.post(`${this.mailingUri}/users/`, {
      ...data,
      org_id,
      user_id,
    });
  }

  revokeUserAccess(record_id): Observable <any> {
    return this.http.delete(`${this.mailingUri}/users/${record_id}`);
  }

  saveImapUser(data: any) {
    const { org_id, user_id } = this;
    return this.http.post(`${this.mailingUri}/imap/setting/user`, {
      ...data,
      org_id,
      user_id,
    });
  }

  /** */
  getAttachmentUrl() {
    return `${this.files_uri}/attachments`;
  }

  getAttachment(name) {
    return this.http.get(`${this.files_uri}/attachments/${name}`);
  }

  getAttachmentBase64String(name) {
    return this.http.get(`${this.files_uri}/attachments/base64/${name}`);
  }

  downloadAttachment(name) {
    return this.http.get(`${this.files_uri}/attachments/download/${name}`, {
      responseType: 'arraybuffer'
    });
  }
  uploadAttachment(data) {
    return this.http.post(`${this.files_uri}/attachments/new`, data, {
      responseType: 'blob', reportProgress: true, observe: 'events'
    });
  }

  sendMail(data: any[], service: string) : Observable<any> {
    return this.http.post(`${this.mailingUri}/users/mail/${service}`, {
      ...data,
    });
  }

  sendSalesItemMail(data, type: string) {
    return this.http.post(
      `${this.mailingUri}/sales/${type.toLowerCase()}`,
      { ...data }
    );
  }


  /** */
  filterThreads(query: any): Observable<any> {
    return this.http.post(
      `${this.mailingUri}/threads`,
      { ...query }
    );
  }

  filterMessages(query: any) {
    return this.http.post(`${this.mailingUri}/messages`, { ...query });
  }
  getRecentEmailActivity(dealCode): Observable<any> {
    const query = {'deal.code': dealCode, org_id: this.org_id, page:0, population: 1}
    return this.filterThreads(query).pipe(mergeMap((response: any) => {
      const threadId = response && response.payload.length ? response.payload[0].threadId : '';
      const msqQuery = {threadId, org_id: this.org_id}
      return this.filterMessages(msqQuery)
    }))
  }
  getLastActivity(body): Observable<any>  {
    const { TimeStToDays } = this.dateUtil;
    return this.http.post(`${this.mailingUri}/messages/activity`, body).pipe(map((data: any) => {
      const lists: any[] = data && data.success ? data.payload : [];
      const result = lists.map(list => ({dealCode: list.dealId, lastDaysAgo: TimeStToDays(list.lastActivity)}));
      return result
    }))
  } 
  filterAttachments(query: string) {
    return this.http.get(`${this.getAttachmentUrl()}?${query}`);
  }

  /** */
  updateMessage(payload: any) {
    return this.http.put(`${this.mailingUri}/messages/${payload.id}`, { ...payload, });
  }

  /** IMAP CONFIGURATION */
  createImapSetting(data) {
    return this.http.post(`${this.imap_server_uri}`, { ...data, org_id: this.org_id });
  }

  deactivateImapSettings(id) {
    return this.http.delete(`${this.imap_server_uri}/${id}`);
  }

  fetchImapSettings() {
    return this.http.get(`${this.imap_server_uri}?org_id=${this.org_id}`);
  }

  updateImapSetting(data) {
    return this.http.put(`${this.imap_server_uri}/${data.id}`, { ...data });
  }

  /** DEALS */
  fetchDeals(name: string) {
    return this.http.get(
      `${this.deals_uri}/${name}/${this.org_id}/getDealSuggestions`
    );
  }
}
